import * as React from 'react';
import { Menu, MenuItem, Button, Divider } from '@mui/material';
import { Link } from 'react-router-dom';

/** A single entry in a {@link MenuBarButton}'s menu, or a divider between entries. */
interface IMenuItem {
    /** Display label; omitted for divider entries. */
    label?: string;
    /** Route target; omitted for divider entries. */
    to?: string;
    /** Renders a {@link Divider} instead of a menu item when true. */
    divider?: boolean;
}

/** A top-level menu bar entry: a button that opens a dropdown of {@link IMenuItem}s. */
interface IMenuGroup {
    /** Top-level button label. */
    label: string;
    /** Dropdown entries shown when the button is open. */
    menuItems: IMenuItem[];
}

/** Props for {@link MenuBarButton}. */
interface IMenuBarButtonProps {
    /** Top-level button label. */
    label: string;
    /** Dropdown entries shown when the button is open. */
    menuItems: IMenuItem[];
    /** Whether the dropdown menu is currently open. */
    open: boolean;
    /** Invoked when the button is clicked to open the dropdown. */
    onOpen: () => void;
    /** Invoked to close the dropdown (backdrop click or item selection). */
    onClose: () => void;
    /** Ref to the button, used as the dropdown's anchor element. */
    anchorEl: React.RefObject<HTMLButtonElement>;
}

const MenuBarButton: React.FC<IMenuBarButtonProps> = ({ label, menuItems, open, onOpen, onClose, anchorEl }) => {
    return (
        <div style={{ cursor: 'pointer' }}>
            <Button ref={anchorEl} onClick={onOpen} aria-haspopup="true"
                style={{ textTransform: 'none', cursor: 'pointer', color: 'inherit' }}
            >{label}</Button>
            <Menu
                open={open}
                onClose={onClose}
                anchorEl={anchorEl.current}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            >
                {menuItems.map(({ label, to, divider }, index) => (
                    divider ? <Divider key={`divider-${index}`} /> : (
                        <MenuItem key={index} component={Link} to={to} onClick={onClose} style={{ cursor: 'pointer' }}>
                            {label}
                        </MenuItem>
                    )
                ))}
            </Menu>
        </div>
    );
};

export default function MenuToolbarExample () {
    const [menuIndex, setMenuIndex] = React.useState<number | null>(null);
    const menuRefs = React.useRef<React.RefObject<HTMLButtonElement>[]>([]);

    const handleOpen = (index: number) => setMenuIndex(index);
    const handleClose = () => setMenuIndex(null);

    const menus: IMenuGroup[] = [
        {
            label: 'Analysis',
            menuItems: [
                { label: 'SM Event Tickets', to: '/SmEventTickets/' },
                { label: 'Order Detail Analysis', to: '/SingleEventAnalysis/' },
                { label: 'TN Sales Analysis', to: '/ExchangeTicketCount/' }
            ]
        },
        {
            label: 'Orders',
            menuItems: [
                { label: 'Vivid Orders', to: '/Vivid/' },
                { label: 'TicketNetwork Orders', to: '/TicketNetwork/' },
                { label: 'GoTickets Orders', to: '/GoTickets/' },
                { label: 'Resale Orders', to: '/Resale/' },
                { divider: true },
                { label: 'Shipping Report', to: '/ShippingReport/' },
                { label: 'Order Mistaken Report', to: '/OrderMistakeReport/' },
                { label: 'Order Auto Price Report', to: '/OrderAutoPriceReport/' },
                { label: 'One Call Now Report', to: '/OneCallNow/' },
                { divider: true },
                { label: 'Order Adjust Rule', to: '/OrderAdjustRule/' },
                { divider: true },
                { label: 'ThermalStep Orders', to: '/ThermalStep/' },
            ]
        },
        {
            label: 'Listings',
            menuItems: [
                { label: 'Listings', to: '/Listings/' },
                { label: 'Event Delivery', to: '/EventDelivery/' },
                { label: 'Row Range for SPEC', to: '/NgaVenueConfig/' },
                { divider: true },
                { label: 'Event Mapping', to: '/ListingsMapping/' },
                { label: 'Event Matcher', to: '/EventMatcher/' },
                { divider: true },
                { label: 'List variable', to: '/ListingVariable/' },
                { label: 'Source Variables', to: '/SourceVariable/' },
                { divider: true },
                { label: 'Additional Listings', to: '/AdditionalListings/' },
                { label: 'Additional Listings Issues', to: '/AdditionalListingsIssues/' }
            ]
        },
        {
            label: 'Payments',
            menuItems: [
                { label: 'Credit Cards', to: '/CreditCards/' },
                { label: 'Unified Credit Transactions', to: '/UnifiedCreditTransactions/' },
                { divider: true },
                { label: 'Divvy Transactions', to: '/DivvyTransaction/' },
                { label: 'Emburse Transactions', to: '/EmburseTransaction/' },
            ]
        },
        {
            label: 'Reports',
            menuItems: [
                { label: 'Order Summary Report', to: '/OrderSummaryReport/' },
                { label: 'Order Entry Report', to: '/OrderEntryReport/' },
                { label: 'Resale Orders Report', to: '/ResaleOrdersReport/' },
                { divider: true },
                { label: 'Process Tracking Report', to: '/ProcessTrackingReport/' },
                { label: 'Problem Order Tracking Report', to: '/ProblemOrderTrackingReport/' },
                { label: 'Difficult Event Tracking Report', to: '/DifficultEventTrackingReport/' },
                { label: 'Mapping Error Report', to: '/MappingErrorReport/' },
                { label: 'Tracking Failed Orders', to: '/TrackingFailedOrders/' },
                { divider: true },
                { label: 'Minute By Minute', to: '/MinuteByMinute/' },
                { label: 'TN Competitive Analysis', to: '/TnCompetitiveAnalysis/' },
                { label: 'TN Hourly Data', to: '/TNHourlyData/' },
                { label: 'Last Minute Underserved Events', to: '/LastMinuteUnderservedEvents/' }
            ]
        },
        {
            label: 'Tools',
            menuItems: [
                { label: 'Email Search Tool', to: '/EmailSearchTool/' },
                { label: 'Email', to: '/Email/' },
                { label: 'Browser Stealth', to: '/Stealth/' },
                { divider: true },
                { label: 'Csv Merge', to: '/CsvMerge/' },
                { label: 'Dynamic Adjustment Rule', to: '/DynamicAdjustRule/' },
                { label: 'Event Comparison Tool', to: '/SmEventCheck/' },
                { label: 'Listing visibility', to: '/ListingVisibility/' },
                { divider: true },
                { label: 'Receipts Counter', to: '/ReceiptsCounter/' },
                { label: 'Ticket Search', to: '/SearchTicketReceipt/' },
                { label: 'TM Processing Report', to: '/AccountReport/' },
                { divider: true },
                { label: 'Inventory Drop Analysis (Crawl)', to: '/InventoryDropAnalysis/' },
            ]
        },
        {
            label: 'Admins',
            menuItems: [
                { label: 'Primary Accounts', to: '/PrimaryAccount/' },
                { label: 'Vivid Peformance Tracking', to: '/VividPerformance/' },
                { divider: true },
                { label: 'Users', to: '/User/' },
                { label: 'Users Dashboard', to: '/UserDashboard/' },
                { label: 'Weekly Working Time', to: '/WeeklyWorkTime/' },
                { label: 'Order Assignments', to: '/OrderAssignments/' },
                { divider: true },
                { label: 'AAT Primary Accounts', to: '/AAT_PrimaryAccount/' },
                { divider: true },
                { label: 'Unify Venue', to: '/MasterVenues/' },
            ]
        },
        {
            label: 'Real-Tickets',
            menuItems: [
                { label: 'Analysis', to: '/Analysis/' },
                { label: 'Analysis history', to: '/AnalysisHistorical/' },
                { divider: true },
                { label: 'Categories', to: '/RTP_Categories/' },
                { label: 'Performers', to: '/RTP_Performers/' },
                { divider: true },
                { label: 'Venues', to: '/RTP_Venues/' },
            ]
        },
    ];

    return (
        <nav style={{ display: 'flex' }}>
            {menus.map((menu, index) => {
                if (!menuRefs.current[index]) menuRefs.current[index] = React.createRef<HTMLButtonElement>();
                return (
                    <MenuBarButton
                        key={index}
                        label={menu.label}
                        menuItems={menu.menuItems}
                        open={menuIndex === index}
                        onOpen={() => handleOpen(index)}
                        onClose={handleClose}
                        anchorEl={menuRefs.current[index]}
                    />
                );
            })}
        </nav>
    );
}